import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { getWhatsAppSettings, updateWhatsAppSettings, getWhatsAppClicks } from '../data/api';

const WhatsAppTab = () => {
  const [settings, setSettings] = useState(null);
  const [clicks, setClicks] = useState({ total: 0, byPage: {}, recent: [] });
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');

  useEffect(() => {
    getWhatsAppSettings().then(setSettings).catch(() => {});
    getWhatsAppClicks().then(setClicks).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus('');
    try {
      await updateWhatsAppSettings(settings);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(''), 2000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <p className="text-gray-500">Loading...</p>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-bold text-gray-900">WhatsApp Settings</h2>
        <Button onClick={handleSave} disabled={saving} data-testid="save-whatsapp-settings" className="bg-green-600 text-white">
          {(() => {
            if (saving) return 'Saving...';
            if (saveStatus === 'saved') return 'Saved!';
            if (saveStatus === 'error') return 'Save Failed';
            return 'Save Settings';
          })()}
        </Button>
      </div>

      <div className="bg-white border rounded-xl p-5 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Phone Number</label>
            <Input value={settings.phoneNumber} onChange={(e) => setSettings({ ...settings, phoneNumber: e.target.value })} data-testid="wa-phone" placeholder="6586618699" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Contact Name</label>
            <Input value={settings.contactName} onChange={(e) => setSettings({ ...settings, contactName: e.target.value })} data-testid="wa-contact-name" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Company Label</label>
            <Input value={settings.companyLabel} onChange={(e) => setSettings({ ...settings, companyLabel: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Response Time Text</label>
            <Input value={settings.responseTime} onChange={(e) => setSettings({ ...settings, responseTime: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">Default Message (pre-filled when user opens chat)</label>
          <Textarea value={settings.defaultMessage} onChange={(e) => setSettings({ ...settings, defaultMessage: e.target.value })} rows={2} data-testid="wa-default-message" />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">Greeting Message (shown in chat widget popup)</label>
          <Textarea value={settings.greetingMessage} onChange={(e) => setSettings({ ...settings, greetingMessage: e.target.value })} rows={2} data-testid="wa-greeting" />
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" checked={settings.enabled} onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })} className="rounded" data-testid="wa-enabled" />
          <label className="text-sm text-gray-700">Enable floating WhatsApp button</label>
        </div>
      </div>

      <div className="bg-white border rounded-xl p-5">
        <h3 className="text-base font-bold text-gray-900 mb-3">Engagement Analytics</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-green-700">{clicks.total}</p>
            <p className="text-xs text-green-600">Total Clicks</p>
          </div>
          {Object.entries(clicks.byPage || {}).slice(0, 3).map(([page, count]) => (
            <div key={page} className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-gray-900">{count}</p>
              <p className="text-xs text-gray-500 capitalize">{page.replace('-', ' ')}</p>
            </div>
          ))}
        </div>
        {clicks.recent && clicks.recent.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Recent Clicks</h4>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {clicks.recent.map((c) => (
                <div key={c.createdAt} className="flex items-center justify-between text-xs text-gray-600 py-1 border-b border-gray-100">
                  <span className="capitalize">{c.page}</span>
                  <span className="text-gray-400 truncate max-w-[200px] mx-2">{c.context}</span>
                  <span className="text-gray-400">{new Date(c.createdAt).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppTab;
